import { _ } from "extensions/underscore";
import { Backbone } from "extensions/backbone";
import $ from "jquery";
import Tokenfield from "bootstrap-tokenfield-jquery3";
import Radio from "backbone.radio";

import { TypeaheadView } from "common/components/typeahead";

import { references } from "common/helpers/references";

import "./tokenfield.scss";

// Override createToken method to accept new token type
Tokenfield.prototype.createToken = function(attrs, triggerChange = true) {
    if (typeof attrs === "string") {
        attrs = {
            value: attrs,
            label: attrs
        };
    } else {
        // Copy objects to prevent contamination of data sources.
        attrs = {
            ...attrs
        };
    }

    // Normalize label and value
    attrs.value = attrs.value.toString().trim();
    attrs.label = attrs.label && attrs.label.length ? attrs.label.trim() : attrs.value;

    // Bail out if has no value or label, or label is too short
    if (!attrs.value.length || !attrs.label.length || attrs.label.length <= this.options.minLength) return;

    // Bail out if maximum number of tokens is reached
    if (this.options.limit && this.getTokens().length >= this.options.limit) return;

    // Allow changing token data before creating it
    const createEvent = $.Event("tokenfield:createtoken", { attrs });
    this.$element.trigger(createEvent);
 
    // Bail out if there if attributes are empty or event was defaultPrevented
    if (!createEvent.attrs || createEvent.isDefaultPrevented()) {
        return;
    }

    const $token = $(this.options.tokenTemplate).data("attrs", attrs);

    // Insert token into HTML
    if (this.$input.hasClass("tt-input")) {
        // If the input has typeahead enabled, insert token before it's parent
        this.$input.parent().before($token);
    } else {
        this.$input.before($token);
    }

    // Temporarily set input width to minimum
    this.$input.css("width", `${this.options.minWidth}px`);

    const $tokenLabel = $token.find(".token-label");
    const $closeButton = $token.find(".close");
    const $operator = $token.find(".operator");
    const $innerWrapper = $token.find(".token-inner-wrapper");

    // Determine maximum possible token label width
    if (!this.maxTokenWidth) {
        this.maxTokenWidth = this.$wrapper.width() - $closeButton.outerWidth()
            - Number.parseInt($closeButton.css("margin-left"), 10)
            - Number.parseInt($closeButton.css("margin-right"), 10)
            - Number.parseInt($token.css("border-left-width"), 10)
            - Number.parseInt($token.css("border-right-width"), 10)
            - Number.parseInt($token.css("padding-left"), 10)
            - Number.parseInt($token.css("padding-right"), 10)
            - Number.parseInt($tokenLabel.css("border-left-width"), 10)
            - Number.parseInt($tokenLabel.css("border-right-width"), 10)
            - Number.parseInt($tokenLabel.css("padding-left"), 10)
            - Number.parseInt($tokenLabel.css("padding-right"), 10)
            - Number.parseInt($tokenLabel.css("margin-left"), 10)
            - Number.parseInt($tokenLabel.css("margin-right"), 10);
    }

    $tokenLabel.text(attrs.label).css("max-width", `${Math.max(this.maxTokenWidth, 300)}px`);
    
    if (attrs.category && Radio.channel("main").request("get:getters").includesPivot(attrs.category)) {
        $innerWrapper.attr("title", `${Radio.channel("main").request("get:getters").findPivotName(attrs.category)}: ${references.toAlias(attrs.ref, attrs.category, { ref: false })}`);
    }

    // Listen to events on token
    $token.on("mousedown", (e) => {
        if (this._disabled || this._readonly) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.preventDeactivation = true;
    }).on("click", (e) => {
        if (/input|textarea/i.test(e.target.tagName)) {
            return;
        }
        
        if (this._disabled || this._readonly) {
            e.preventDefault();
            e.stopPropagation();
        }

        this.preventDeactivation = false;

        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            this.toggle($token);
        } else {
            this.$element.trigger($.Event("tokenfield:clickedtoken", {
                attrs,
                relatedTarget: $token.get(0),
                originalTarget: e.target
            }));
        }

        this.activate($token, e.shiftKey, e.shiftKey);
    }).on("dblclick", (e) => {
        if (this._disabled || this._readonly || !this.options.allowEditing) {
            e.preventDefault();
            e.stopPropagation();
        } else {
            this.edit($token);
        }
    });

    $closeButton.on("click", $.proxy(this.remove, this));
    
    $operator.on("click", (e) => {
        e.stopPropagation();
        this.$element.trigger($.Event("tokenfield:clickedoperator", {
            attrs,
            relatedTarget: $token.get(0),
            originalTarget: e.target
        }));
    });

    // Trigger createdtoken event on the original field
    // indicating that the token is now in the DOM
    this.$element.trigger($.Event("tokenfield:createdtoken", {
        attrs,
        relatedTarget: $token.get(0)
    }));

    // Trigger change event on the original field
    if (triggerChange) {
        this.$element.val(this.getTokensList()).trigger($.Event("change", { initiator: "tokenfield" }));
    }

    // Update tokenfield dimensions
    this.update();
    
    // Return original element
    // Their stupid code waits for truthy return to clear typeahead input in bootstrap-tokenfield.js:448
    return this.$element.get(0);
};
Tokenfield.prototype.focusInput = function(e) {
    const $target = $(e.target);
    // Don't focus if event occurred on items inside the tokenfield
    if ([".token", ".token-input", ".tt-dropdown-menu", ".pz-dropdown"].some((selector) => $target.closest(selector).length)) {
        return;
    }

    // Focus only after the current call stack has cleared,
    // otherwise has no effect.
    // Reason: mousedown is too early - input will lose focus
    // after mousedown. However, since the input may be moved
    // in DOM, there may be no click or mouseup event triggered.
    setTimeout(() => {
        this.$input.focus();
        this.$element.trigger("tokenfield:showAllCategories");
    }, 0);
};
Tokenfield.prototype.setTokens = function(tokens, add, triggerChange) {
    if (!tokens) {
        return;
    }

    if (!add) {
        this.$wrapper.find(".token").remove();
    }

    if (typeof triggerChange === "undefined") {
        triggerChange = true;
    }

    if (typeof tokens === "string") {
        if (typeof this.options.splitter === "function") {
            tokens = this.options.splitter(tokens);
        } else if (this._delimiters.length) {
            // Split based on delimiters
            tokens = tokens.split(new RegExp(`[${this._delimiters.join("")}]`));
        } else {
            tokens = [tokens];
        }
    }

    if (Array.isArray(tokens)) {
        tokens.forEach((t) => this.createToken(t, triggerChange));
    }

    this.$element.get(0);
};

// Override update function to debounce (many tokens trigger many unnecessary dom updates)
Tokenfield.prototype.update = _.debounce(function(e) {
    let value = this.$input.val();
    const inputPaddingLeft = Number.parseInt(this.$input.css("padding-left"), 10);
    const inputPaddingRight = Number.parseInt(this.$input.css("padding-right"), 10);
    const inputPadding = inputPaddingLeft + inputPaddingRight;

    if (this.$input.data("edit")) {
        if (!value) {
            value = this.$input.prop("placeholder");
        }
        if (value === this.$mirror.text()) {
            return;
        }

        this.$mirror.text(value);

        const mirrorWidth = this.$mirror.width() + 10;
        if (mirrorWidth > this.$wrapper.width()) {
            this.$input.width(this.$wrapper.width());
            return;
        }

        this.$input.width(mirrorWidth);

        if (this.$hint) {
            this.$hint.width(mirrorWidth);
        }
    } else {
        let w;
        if (this.textDirection === "rtl") {
            w = this.$input.offset().left
                + this.$input.outerWidth()
                - this.$wrapper.offset().left
                - Number.parseInt(this.$wrapper.css("padding-left"), 10)
                - Number.parseInt(this.$wrapper.css("padding-right"), 10)
                - inputPadding
                - 1;
        } else {
            w = this.$wrapper.offset().left
                + this.$wrapper.width()
                + Number.parseInt(this.$wrapper.css("padding-left"), 10)
                - this.$input.offset().left
                - inputPadding;
        }

        // some usecases pre-render widget before attaching to DOM,
        // dimensions returned by jquery will be NaN -> we default to 100%
        // so placeholder won't be cut off.

        if (_.isNaN(w)) {
            this.$input.width("100%");
            if (this.$hint) {
                this.$hint.width("100%");
            }
        } else {
            this.$input.width(w);
            if (this.$hint) {
                this.$hint.width(w);
            }
        }
    }
}, 100);

// Override Tab/Enter key keydown
const oldKeydown = Tokenfield.prototype.keydown;
Tokenfield.prototype.keydown = function(e) {
    if (!this.focused) return;
    
    if ([9, 13].includes(e.keyCode)) {
        // We will handle creating tokens from typeahead in typeahead events if there are active suggestions.
        // Ignore events coming from input itself as we should allow searching for arbitrary strings
        if (this.$wrapper.find(".tt-cursor").length) {
            return true;
        }
    
        // Create token
        if ((this.$input.is(document.activeElement) && this.$input.val().length) || this.$input.data("edit")) {
            return this.createTokensFromInput(e, this.$input.data("edit"));
        }
    } else {
        return oldKeydown.apply(this, arguments);
    }
};

export const Model = Backbone.Model.extend({
    defaults: () => ({
        minWidth: 60,
        limit: 0,
        createTokensOnBlur: false,
        placeholder: "",
        tokens: []
    })
});

/**
 * Tokenfield View is a view based on Tokenfield library
 * http://sliptree.github.io/bootstrap-tokenfield/
 */
export const View = TypeaheadView.extend({
    getTokens() {
        return this.ui.input.tokenfield("getTokens");
    },

    getTokensGroups() {
        const tokens = this.getTokens();
        return _.pluck(tokens, "group");
    },

    _getTypeaheadObject() {
        return this.ui.input.data("bs.tokenfield").$input.data("ttTypeahead");
    },

    initTypeahead() {
        const input = this.ui.input;

        // Bind events
        this.typeaheadEvents = {
            // Allow changing token data before creating it
            "tokenfield:createtoken": (event) => {
                this.triggerMethod("token:before:create", event);
            },
            // Do any necessary pre-checks before injecting a token to the DOM
            "tokenfield:createdtoken": (event) => {
                this.triggerMethod("token:create", event);
                this.triggerMethod("updated", event.attrs, this.getTokens());
            },
            "tokenfield:editedtoken": (event) => {
                this.triggerMethod("token:edit", event);
                this.triggerMethod("updated", event.attrs, this.getTokens());
            },
            "tokenfield:removedtoken": (event) => {
                this.triggerMethod("token:delete", event);
                this.triggerMethod("updated", event.attrs, this.getTokens());
            },
            "tokenfield:clickedtoken": (event) => {
                this.triggerMethod("token:click", event);
            },
            "tokenfield:showAllCategories": (event) => {
                this.showAllCategories(event);
            },
            "tokenfield:clickedoperator": (event) => {
                this.triggerMethod("operator:click", event);
            },
            change: (event) => {
                this.triggerMethod("change", {
                    allTokens: this.getTokens()
                });
            }
        };

        input.on(this.typeaheadEvents).tokenfield({
            typeahead: this.collection.getList(),
            minWidth: this.model.get("minWidth"),
            limit: this.model.get("minWidth"),
            createTokensOnBlur: this.model.get("createTokensOnBlur"),
            allowEditing: false,
            tokenTemplate: this.getOption("tokenTemplate"),
            splitter: this.getOption("splitter")
        }).data("bs.tokenfield").$input.on({ // Also propagate Typeahead events up to the original input
            "typeahead:render": _.bind(this.e.checkEmpty, this),
            click: (event) => {
                this.showAllCategories(event);
            },
            keypress: (event) => {
                this.triggerMethod("hide:all", event);
            }
        });

        // Set initial tokens, but don't trigger change event
        input.tokenfield("setTokens", this.model.get("tokens"), true, false);
    },

    showAllCategories(event) {
        if (!this.getTokenfieldInstance().$input.val()) {
            this.triggerMethod("show:all", event);
        }
    },

    addUnappliedToken(tokens) {
        const input = this.ui.input;
       
        this.model.set("tokens", tokens);
        input.tokenfield("setTokens", this.model.get("tokens"), false, false);
    },
    
    getTokenfieldInstance() {
        return this.ui.input.data("bs.tokenfield");
    },

    tokenTemplate: `<div class="token">
                       <span class="token-label"></span>
                       <a href="#" class="close" tabindex="-1">&times;</a>
                    </div>`,

    clear() {
        this.getTokenfieldInstance().$input.val("");
    }
});
